import Article from "@mui/icons-material/Article";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import Code from "@mui/icons-material/Code";
import Delete from "@mui/icons-material/Delete";
import ElectricalServicesIcon from "@mui/icons-material/ElectricalServices";
import ExtensionIcon from "@mui/icons-material/Extension";
import KeyIcon from "@mui/icons-material/Key";
import Note from "@mui/icons-material/Note";
import SettingsInputComponentIcon from "@mui/icons-material/SettingsInputComponent";
import ShowChart from "@mui/icons-material/ShowChart";
import {
  Box,
  FormControl,
  Grid,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Popover,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { CasLoader } from "../../brand/CasLoader";
import { useInsertChartBlock } from "../../../hook/chart";
import { useInsertComponentBlock } from "../../../hook/component";
import { useMenu, UseMenuProps } from "../../../hook/menu";
import { useInsertPageBlock } from "../../../hook/page/block/page";
import { useInsertPushConnectorBlock } from "../../../hook/page/block/pushConnector";
import { useInsertHtmlBlock } from "../../../hook/page";
import { useInsertProtocolBlock } from "../../../hook/protocol";
import React from "react";
import DriveFileMoveIcon from "@mui/icons-material/DriveFileMove";
import {
  useDeleteBlock,
  useMoveBlock,
  useMoveBlockPageOptions,
} from "../../../hook/page/block";
import { NotebookPage } from "../../../types/note";
import StorageIcon from "@mui/icons-material/Storage";
import { AddImplementationMenu } from "./block/implementation";
import { AddCronMenu } from "./block/cron";
import { AddHttpHookMenu } from "./block/httpHook";
import { useApps } from "../../../hook";
import TerminalIcon from "@mui/icons-material/Terminal";
import WebhookIcon from "@mui/icons-material/Webhook";
import { useInsertLogStreamBlock } from "../../../hook/page/block/logStream";
import { useInsertDevcontainerBlock } from "../../../hook/page/block/devcontainer";
import { useInsertVolumeBlock } from "../../../hook/page/block/volume";
import InventoryIcon from "@mui/icons-material/Inventory";
import { useInsertPackageBlock } from "../../../hook/package";

export const AddBlockMenu: React.FC<
  {
    blockIndex: number;
  } & UseMenuProps
> = ({ blockIndex, ...menuProps }) => {
  const { onClose } = menuProps;
  const insertHtmlBlock = useInsertHtmlBlock();
  const insertChartBlock = useInsertChartBlock();
  const insertPageBlock = useInsertPageBlock();
  const insertComponentBlock = useInsertComponentBlock();
  const insertProtocolBlock = useInsertProtocolBlock();
  const insertPushConBlock = useInsertPushConnectorBlock();
  const insertLogStreamBlock = useInsertLogStreamBlock();
  const insertPackageBlock = useInsertPackageBlock();
  const insertDevcontainerBlock = useInsertDevcontainerBlock();
  const insertVolumeBlock = useInsertVolumeBlock();
  const {
    anchorEl: implAnchorEl,
    handleOpen: implHandleOpen,
    onClose: implOnClose,
    open: implOpen,
    setAnchorEl: implSetAnchorEl,
    anchorToClick: implAnchorToClick,
  } = useMenu();
  const {
    anchorEl: cronAnchorEl,
    handleOpen: cronHandleOpen,
    onClose: cronOnClose,
    open: cronOpen,
  } = useMenu();
  const {
    anchorEl: httpHookAnchorEl,
    handleOpen: httpHookHandleOpen,
    onClose: httpHookOnClose,
    open: httpHookOpen,
    anchorToClick: httpHookAnchorToClick,
  } = useMenu();

  return (
    <>
      <AddImplementationMenu
        anchorEl={implAnchorEl}
        onClose={implOnClose}
        open={implOpen}
        anchorToClick={implAnchorToClick}
        blockIndex={blockIndex}
      />
      <AddHttpHookMenu
        anchorEl={httpHookAnchorEl}
        onClose={httpHookOnClose}
        open={httpHookOpen}
        anchorToClick={httpHookAnchorToClick}
        blockIndex={blockIndex}
      />
      <AddCronMenu
        anchorEl={cronAnchorEl}
        onClose={cronOnClose}
        open={cronOpen}
        blockIndex={blockIndex}
        onComplete={onClose}
      />
      <Menu {...menuProps}>
        <MenuItem
          sx={{ width: "15rem" }}
          onClick={(e) => {
            onClose();
            insertHtmlBlock({ blockIndex });
          }}
        >
          <ListItemIcon>
            <Note color="primary" fontSize="small" />
          </ListItemIcon>
          <ListItemText>Text</ListItemText>
        </MenuItem>
        <MenuItem
          sx={{ width: "15rem" }}
          onClick={(e) => {
            onClose();
            insertPageBlock({ blockIndex, isDatabase: true });
          }}
        >
          <ListItemIcon>
            <StorageIcon color="primary" fontSize="small" />
          </ListItemIcon>
          <ListItemText>Database</ListItemText>
        </MenuItem>
        <MenuItem
          sx={{ width: "15rem" }}
          onClick={() => {
            insertChartBlock({ blockIndex });
            onClose();
          }}
        >
          <ListItemIcon>
            <ShowChart color="primary" fontSize="small" />
          </ListItemIcon>
          <ListItemText>Chart</ListItemText>
        </MenuItem>
        <MenuItem
          sx={{ width: "15rem" }}
          onClick={() => {
            insertPageBlock({ blockIndex });
            onClose();
          }}
        >
          <ListItemIcon>
            <Article color="primary" fontSize="small" />
          </ListItemIcon>
          <ListItemText>Page</ListItemText>
        </MenuItem>
        <MenuItem
          sx={{ width: "15rem" }}
          onClick={() => {
            insertComponentBlock({ blockIndex });
            onClose();
          }}
        >
          <ListItemIcon>
            <ExtensionIcon color="primary" fontSize="small" />
          </ListItemIcon>
          <ListItemText>Component</ListItemText>
        </MenuItem>
        <MenuItem
          sx={{ width: "15rem" }}
          onClick={() => {
            insertProtocolBlock({ blockIndex });
            onClose();
          }}
        >
          <ListItemIcon>
            <SettingsInputComponentIcon color="primary" fontSize="small" />
          </ListItemIcon>
          <ListItemText>Protocol</ListItemText>
        </MenuItem>
        <MenuItem
          sx={{ width: "15rem" }}
          onClick={(e) => {
            implHandleOpen(e);
          }}
        >
          <ListItemIcon>
            <ElectricalServicesIcon color="primary" fontSize="small" />
          </ListItemIcon>
          <ListItemText>Implementation</ListItemText>
        </MenuItem>
        <MenuItem
          sx={{ width: "15rem" }}
          onClick={(e) => {
            insertPushConBlock({ blockIndex });
            onClose();
          }}
        >
          <ListItemIcon>
            <KeyIcon color="primary" fontSize="small" />
          </ListItemIcon>
          <ListItemText>Credential</ListItemText>
        </MenuItem>
        <MenuItem
          data-testid="add-log-stream-block"
          sx={{ width: "15rem" }}
          onClick={(e) => {
            insertLogStreamBlock({ blockIndex });
            onClose();
          }}
        >
          <ListItemIcon>
            <TerminalIcon color="primary" fontSize="small" />
          </ListItemIcon>
          <ListItemText>Log Stream</ListItemText>
        </MenuItem>
        <MenuItem
          sx={{ width: "15rem" }}
          onClick={() => {
            insertPackageBlock({ blockIndex });
            onClose();
          }}
        >
          <ListItemIcon>
            <InventoryIcon color="primary" fontSize="small" />
          </ListItemIcon>
          <ListItemText>Package</ListItemText>
        </MenuItem>
        <MenuItem
          data-testid="add-cron-block"
          sx={{ width: "15rem" }}
          onClick={cronHandleOpen}
        >
          <ListItemIcon>
            <AccessTimeIcon color="primary" fontSize="small" />
          </ListItemIcon>
          <ListItemText>Cron</ListItemText>
        </MenuItem>
        <MenuItem
          sx={{ width: "15rem" }}
          onClick={(e) => {
            httpHookHandleOpen(e);
          }}
        >
          <ListItemIcon>
            <WebhookIcon color="primary" fontSize="small" />
          </ListItemIcon>
          <ListItemText>HTTP Hook</ListItemText>
        </MenuItem>
        <MenuItem
          data-testid="add-devcontainer-block"
          sx={{ width: "15rem" }}
          onClick={() => {
            insertDevcontainerBlock({ blockIndex });
            onClose();
          }}
        >
          <ListItemIcon>
            <TerminalIcon color="primary" fontSize="small" />
          </ListItemIcon>
          <ListItemText>Devcontainer</ListItemText>
        </MenuItem>
        <MenuItem
          data-testid="add-volume-block"
          sx={{ width: "15rem" }}
          onClick={() => {
            insertVolumeBlock({ blockIndex });
            onClose();
          }}
        >
          <ListItemIcon>
            <StorageIcon color="primary" fontSize="small" />
          </ListItemIcon>
          <ListItemText>Volume</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

export const MoveBlockMenu: React.FC<
  {
    moveFunction: (destinationPage: NotebookPage) => any;
  } & UseMenuProps
> = React.memo(({ moveFunction, ...menuProps }) => {
  const { onClose, open } = menuProps;
  const {
    searchString,
    setSearchString,
    isLoading,
    pages,
    setSelectedAppId,
    selectedAppId,
  } = useMoveBlockPageOptions({ isOpen: open });
  const [apps] = useApps();

  return (
    <Popover {...menuProps}>
      <Box sx={{ width: "30rem" }}>
        <Box sx={{ padding: 3 }}>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="app-select-label">Select App</InputLabel>
                <Select
                  labelId="app-select-label"
                  id="app-select"
                  value={selectedAppId}
                  label="Select App"
                  onChange={(e) => {
                    setSelectedAppId(e.target.value);
                  }}
                >
                  {apps.map((app) => (
                    <MenuItem key={app.id} value={app.id}>
                      {app.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6">Move To Page</Typography>
            </Grid>
            <Grid item xs={10}>
              <TextField
                sx={{ width: "100%" }}
                value={searchString}
                onChange={(e) => setSearchString(e.target.value)}
              />
            </Grid>
            <Grid item xs={2}>
              {isLoading && <CasLoader />}
            </Grid>
          </Grid>
        </Box>
        <Box sx={{ height: "15rem", overflowY: "scroll" }}>
          <List>
            {pages.map((page) => (
              <ListItem key={page["page-id"]} sx={{ padding: 0 }}>
                <ListItemButton
                  onClick={() => {
                    moveFunction(page);
                    onClose();
                  }}
                >
                  <ListItemText>{page["page-data"].name}</ListItemText>
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      </Box>
    </Popover>
  );
});

export const BlockActionMenu: React.FC<
  {
    blockIndex: number;
  } & UseMenuProps
> = ({ blockIndex, ...menuProps }) => {
  const { onClose } = menuProps;
  const { deleteBlock } = useDeleteBlock();
  const {
    anchorEl: anchorElMove,
    handleOpen: handleOpenMove,
    onClose: onCloseMove,
    open: openMove,
    setAnchorEl: setAnchorElMove,
  } = useMenu();
  const moveBlock = useMoveBlock();
  const moveFunction = (page: NotebookPage) => moveBlock({ blockIndex, page });

  return (
    <>
      <MoveBlockMenu
        anchorEl={anchorElMove}
        moveFunction={moveFunction}
        onClose={onCloseMove}
        open={openMove}
      />
      <Menu {...menuProps}>
        <MenuItem
          sx={{ width: "15rem" }}
          onClick={(e) => {
            deleteBlock(blockIndex);
            onClose();
          }}
        >
          <ListItemIcon>
            <Delete color="primary" fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
        <MenuItem
          sx={{ width: "15rem" }}
          onClick={(e) => {
            handleOpenMove(e);
            // onClose();
          }}
          // ref={(e) => {
          //   setAnchorElMove(e);
          // }}
        >
          <ListItemIcon>
            <DriveFileMoveIcon color="primary" fontSize="small" />
          </ListItemIcon>
          <ListItemText>Move</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};
